import React, { useEffect, useMemo, useState, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import Editor from '@draft-js-plugins/editor';
import createEmojiPlugin from '@draft-js-plugins/emoji';
import createLinkifyPlugin from '@draft-js-plugins/linkify';
import createMentionPlugin, { defaultTheme } from '@draft-js-plugins/mention';
import styled from '@emotion/styled';
import { KeyBindingUtil, getDefaultKeyBinding } from 'draft-js';
import { useDispatch } from 'react-redux';
import { HideScrollbarCSS } from 'styles/common';
import { baseTheme } from 'themes/clients/baseTheme';

import { Emoji } from 'assets/assetsV1/iconComponents';

import { SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';

import { showErrorSnackbar } from 'services/snackbar';

import { Button } from 'uiToolkitV1/Button';

import { CompetencySuggestions } from './components/CompetencySuggestions/CompetencySuggestions';
import competencySuggestionsCSS from './components/CompetencySuggestions/styles/competencySuggestions.module.css';
import { MentionSuggestions } from './components/MentionSuggestions/MentionSuggestions';
import mentionSuggestionsCSS from './components/MentionSuggestions/styles/mentionSuggestions.module.css';
import emojiSuggestionsCSS from './styles/emoji.module.css';
import linkCSS from './styles/link.module.css';

const EmojiIcon = () => <Emoji fill="transparent" />;

const { hasCommandModifier } = KeyBindingUtil;

const ENTER_COMMAND = 'onEnter';
const ESCAPE_COMMAND = 'onEscape';

const linkifyPlugin = createLinkifyPlugin({
  target: '_blank',
  theme: linkCSS,
});

const EditorComp = ({
  plugins,
  editorState,
  focusOnMount,
  updateEditorState,
  placeholder,
  readOnly,
  onEnter,
  onEscape,
  allowNewLine,
  allowEnterToNextLine = false,
  editorRef,
  handleBlur = () => {},
  handleFocus = () => {},
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (focusOnMount) {
      editorRef?.current?.focus();
    }
  }, [focusOnMount, editorRef]);

  const onChangeHandler = (editorState) => {
    updateEditorState(editorState);
  };

  const handleKeyCommand = (command) => {
    if (command === ENTER_COMMAND) {
      if (!hasError) {
        if (onEnter) {
          onEnter();
        }
      } else {
        dispatch(showErrorSnackbar(errorMessage));
      }
      return 'handled';
    }
    if (command === ESCAPE_COMMAND) {
      if (onEscape) {
        onEscape();
      }
      return 'handled';
    }
    return 'not-handled';
  };

  // REFER: https://draftjs.org/docs/advanced-topics-key-bindings#example
  // REFER: https://github.com/draft-js-plugins/draft-js-plugins/blob/master/FAQ.md#keybindings-added-by-a-plugin-stop-working-when-using-custom-keybindingfn-function
  const keyBindingFn = (e) => {
    // if enter is pressed and new line is not allowed, consider it an enter command
    if (e.keyCode === 13 && !allowNewLine) {
      return ENTER_COMMAND;
    }
    // if enter is pressed and ctrl or cmd is not pressed, consider it an enter command
    if (e.keyCode === 13 && !hasCommandModifier(e) && !allowEnterToNextLine) {
      return ENTER_COMMAND;
    }
    if (e.keyCode === 27) {
      return ESCAPE_COMMAND;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      return undefined;
    }
    return getDefaultKeyBinding(e);
  };

  return (
    <Editor
      ref={editorRef}
      editorState={editorState}
      onChange={onChangeHandler}
      plugins={plugins}
      placeholder={placeholder}
      readOnly={readOnly}
      handleKeyCommand={handleKeyCommand}
      keyBindingFn={keyBindingFn}
      spellCheck
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

const PostField = ({
  useMentionFunctionality = true,
  useHashtagFunctionality = true,
  useEmojiFunctionality = false,
  focusOnMount,
  updateEditorState,
  placeholder = 'What are you thinking about?',
  readOnly,
  onEnter,
  onEscape,
  allowNewLine = false,
  allowEnterToNextLine = false,
  editorState,
  widgetType = SEARCH_WIDGET_TYPES.search,
  height,
  className = '',
  editorStyles,
  editorChakraStyles = {},
  placeholderStyles,
  usePortalForSuggestions = false,
  portalParentRef,
  setEmojiPlugin = () => {},
  mentionExclusions = [],
  emojiButtonHeight = '40px',
  handleBlur = () => {},
  handleFocus = () => {},
}) => {
  // REFER: https://github.com/draft-js-plugins/draft-js-plugins/issues/1234#issuecomment-1072038701
  // Why it's essential to memoize this
  // basically on re-renders it's losing context of editor state in plugins as they're are internally connected instead of us passing
  // editor state to them
  const {
    mentionPlugin,
    hashTagPlugin,
    emojiPlugin,
    DraftJSMentionSuggestions,
    CompetencyMentionSuggestions,
    EmojiSelect,
  } = useMemo(() => {
    // User mention plugin ( @ )
    const mentionPlugin = createMentionPlugin({
      // mentionComponent: MentionComponent
      mentionPrefix: '@',
      mentionTrigger: '@',
      entityMutability: 'IMMUTABLE', // <- this controls whether mentioned entity can be broken e.g. "@John Doe" can be broken down to just "@John" with backspace and still represent the user/entity "John Doe"
      theme: {
        ...defaultTheme,
        ...mentionSuggestionsCSS,
      },
      popperOptions: {
        placement: 'bottom-start',
      },
    });
    const { MentionSuggestions: DraftJSMentionSuggestions } = mentionPlugin;
    // competency plugin ( # )
    // this is a mention plugin only, not to be confused with createHashTagPlugin
    // createHashTagPlugin is just plain text with "#" in prefix
    // this allows us to make api call on writing "#"
    const hashTagPlugin = createMentionPlugin({
      mentionTrigger: '#',
      mentionPrefix: '#',
      entityMutability: 'IMMUTABLE',
      theme: {
        ...defaultTheme,
        ...competencySuggestionsCSS,
      },
      popperOptions: {
        placement: 'bottom-start',
      },
    });
    const { MentionSuggestions: CompetencyMentionSuggestions } = hashTagPlugin;

    // emoji plugin
    const emojiPlugin = createEmojiPlugin({
      useNativeArt: true,
      theme: {
        ...emojiSuggestionsCSS,
        emojiSelectPopover: `${emojiSuggestionsCSS.emojiSelectPopover} emojiSelectPopover-postfield`,
      },
      selectButtonContent: (
        <Button
          sx={{
            svg: {
              color: `${baseTheme.colors.text.g2} !important`,
              boxSize: '24px',
            },
          }}
          variant="tertiary"
          StartIcon={EmojiIcon}
          height={emojiButtonHeight}
        />
      ),
    });
    const { EmojiSelect } = emojiPlugin;
    return {
      mentionPlugin,
      hashTagPlugin,
      DraftJSMentionSuggestions,
      CompetencyMentionSuggestions,
      emojiPlugin,
      EmojiSelect,
    };
  }, []);

  const editorRef = useRef();

  const focusEditor = () => {
    editorRef?.current?.focus();
  };

  const checkIsHashtagFunctionalityEnabled = () => {
    if (useHashtagFunctionality) {
      if (['POST', 'COMMENT'].includes(widgetType)) {
        return true;
      }
      return true;
    }
    return false;
  };

  const plugins = [linkifyPlugin, hashTagPlugin, mentionPlugin, emojiPlugin];

  useEffect(() => {
    if (useEmojiFunctionality) {
      setEmojiPlugin(emojiPlugin);
    }
  }, [useEmojiFunctionality, setEmojiPlugin, emojiPlugin]);

  if (!editorState) {
    return null;
  }

  return (
    <EditorContainer
      editorStyles={editorStyles}
      placeholderStyles={placeholderStyles}
      className={`${className} editorContainer`}
      onClick={focusEditor}
      height={height}
      {...editorChakraStyles}
    >
      <EditorComp
        editorState={editorState}
        plugins={plugins}
        focusOnMount={focusOnMount}
        updateEditorState={updateEditorState}
        placeholder={placeholder}
        readOnly={readOnly}
        onEnter={onEnter}
        onEscape={onEscape}
        allowNewLine={allowNewLine}
        allowEnterToNextLine={allowEnterToNextLine}
        editorRef={editorRef}
        handleBlur={handleBlur}
        handleFocus={handleFocus}
      />
      {useMentionFunctionality && (
        <MentionSuggestions
          DraftJSMentionSuggestions={DraftJSMentionSuggestions}
          widgetType={widgetType}
          usePortalForSuggestions={usePortalForSuggestions}
          portalParentRef={portalParentRef}
          exclusions={mentionExclusions}
        />
      )}
      {checkIsHashtagFunctionalityEnabled() && (
        <CompetencySuggestions
          CompetencyMentionSuggestions={CompetencyMentionSuggestions}
          widgetType={widgetType}
          usePortalForSuggestions={usePortalForSuggestions}
          portalParentRef={portalParentRef}
        />
      )}
      {/* {useEmojiFunctionality && <EmojiSelect />} */}
    </EditorContainer>
  );
};

const EditorContainer = styled(Box, {
  shouldForwardProp: (prop) => !['editorStyles', 'placeholderStyles'].includes(prop),
})`
  min-width: 0;
  flex: auto 1 1;
  padding: 11px;
  cursor: text;
  & .DraftEditor-editorContainer {
    font-family: ${({ theme }) => theme.fonts.body} !important;
    color: ${({ theme }) => theme.colors.text.g1};
    span[title^=':'] {
      font-family: Segoe UI Emoji;
    }
    ${(props) => props.editorStyles};
    ${HideScrollbarCSS};
  }
  & .public-DraftEditorPlaceholder-inner {
    font-size: 14px;
    position: absolute;
    color: ${(props) => props.theme.colors.text.g3};
    pointer-events: none;
    font-family: ${({ theme }) => theme.fonts.body} !important;
  }
`;

export { PostField };
